import React, { useState, useEffect } from 'react';
import { Spinner, Button, Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Button as PButton } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../../DataTables/DataTableBase';
import { useTranslation } from "react-i18next";
import {
    getDictionariesAction,
    addDictionaryAction,
    updateDictionaryAction,
    deleteDictionaryAction,
    downloadDictionaryTemplateAction
} from '../../../../store/actions/AdministrationAction';

const DictionaryTab = ({ admin, showLoading, showProvLoading, hasMenu, canDo, dispatch }) => {
    const { t } = useTranslation();
    let errorsObj = { nameFr: '', code: '', reference: '' };
    
    const [nameFr, setNameFr] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [code, setCode] = useState('');
    const [reference, setReference] = useState('');
    const [parent, setParent] = useState('');
    const [errors, setErrors] = useState(errorsObj);
    const [updateMode, setUpdateMode] = useState(false);
    const [dataUpdateId, setDataUpdateId] = useState(null);
    const [addModal, setAddModal] = useState(false);
    const [dictionaries, setDictionaries] = useState([]);

    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    // Get unique references for filter options
    const uniqueReferences = [...new Set(dictionaries.map(dict => dict.reference))].map(ref => ({
        label: ref,
        value: ref
    }));

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            reference: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        });
        setGlobalFilterValue("");
    };

    const renderHeader = () => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <PButton 
                    type="button" 
                    icon="pi pi-filter-slash"  
                    label={t('common.reinit')} 
                    outlined 
                    onClick={clearFilter}
                    className="me-2"
                    size="small"
                />
                <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={(e) => onGlobalFilterChange(e)} 
                        placeholder={t('common.search')}
                        size="small"
                    />
                </div>
            </div>
        );
    };

    const clearFilter = () => {
        initFilters();
    };

    const setColumns = () => {
        return [
            {name: 'code', headerName: t('common.code'), filterPlaceholder: t('common.filter-code'), style: { minWidth: '8rem' }, filter: true, bodyTemplate: codeBodyTemplate},
            {name: 'reference', headerName: t('common.reference'), filterPlaceholder: t('common.filter-reference'), style: { minWidth: '10rem' }, filter: true, bodyTemplate: referenceBodyTemplate},
            {name: 'nameFr', headerName: t('common.name-fr'), filterPlaceholder: t('common.filter-name'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'nameEn', headerName: t('common.name-en'), filterPlaceholder: t('common.filter-name-en'), style: { minWidth: '10rem' }, filter: false, bodyTemplate: nameEnBodyTemplate},
            {name: 'parent', headerName: t('common.parent'), filterPlaceholder: t('common.filter-parent'), style: { minWidth: '8rem' }, filter: false, bodyTemplate: parentBodyTemplate},
            {name: 'createdAt', filterField: 'createdAt', dataType: 'date', headerName: t('common.created-on'), filterPlaceholder: t('common.filter-by-creation'), style: { minWidth: '8rem' }, filter: true, bodyTemplate: datePaiementBodyTemplate, filterElement: dateFilterTemplate},
            {name: 'actions', headerName: 'Actions', filterPlaceholder: '', style: { minWidth: '8rem'}, filter: false, bodyTemplate: getActionsTemplate},
        ]
    };

    const codeBodyTemplate = (rowData) => {
        return rowData.code
    };

    const referenceBodyTemplate = (rowData) => {
        return rowData.reference
    };

    const nameEnBodyTemplate = (rowData) => {
        return rowData.nameEn || '-';
    };

    const parentBodyTemplate = (rowData) => {
        return rowData.parent || '-';
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const getActionsTemplate = (rowData) => {
        return (
            <div className="d-flex justify-content-center gap-1">
                {hasMenu('MENPARAM') && canDo('MENPARAM', 'EDITDICTIONARY') && (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => updateData(rowData)}
                        title={t('common.edit')}
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                )}
                {hasMenu('MENPARAM') && canDo('MENPARAM', 'DELETEDICTIONARY') && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(rowData)}
                        title={t('common.delete')}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    };

    const handleDelete = (rowData) => {
        swal({
            title: t('common.delete-the') + "dictionnaire " + rowData.nameFr + " ?",
            text: t('common.once') + "dictionnaire" + t('common.content-deleted'),
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                dispatch(deleteDictionaryAction(rowData.id)).then(() => {
                    loadDictionaries();
                });
            }
        });
    };

    const updateData = (data) => {
        setNameFr(data.nameFr || '');
        setNameEn(data.nameEn || '');
        setCode(data.code || '');
        setReference(data.reference || '');
        setParent(data.parent || '');
        setUpdateMode(true);
        setDataUpdateId(data.id);
        setAddModal(true);
    };

    const dateFilterTemplate = (options) => {
        return <Calendar 
            value={options.value} 
            onChange={(e) => options.filterCallback(e.value, options.index)} 
            dateFormat="dd/mm/yy" 
            placeholder="dd/mm/yyyy" 
            mask="99/99/9999" 
        />;
    };

    const datePaiementBodyTemplate = (rowData) => {
        return formatDate(rowData.createdAt);
    };

    const formatDate = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleDownloadTemplate = () => {
        dispatch(downloadDictionaryTemplateAction());
    };

    const resetForm = () => {
        setNameFr('');
        setNameEn('');
        setCode('');
        setReference('');
        setParent('');
        setErrors(errorsObj);
        setUpdateMode(false);
        setDataUpdateId(null);
    };

    const loadDictionaries = async () => {
        try {
            const response = await dispatch(getDictionariesAction());
            if (response && response.data) {
                setDictionaries(response.data);
            }
        } catch (error) {
            console.error('Error loading dictionaries:', error);
        }
    };

    useEffect(() => {
        initFilters();
        loadDictionaries();
    }, []);

    function onDictionarySave(e) {
        e.preventDefault();
        let error = false;
        const errorObj = { ...errorsObj };
        
        if (nameFr === '') {
            errorObj.nameFr = t('common.name-required');
            error = true;
        }
        
        if (code === '') {
            errorObj.code = t('common.code-required');
            error = true;
        }
        
        if (reference === '') {
            errorObj.reference = t('common.reference-required');
            error = true;
        }
        
        setErrors(errorObj);
        if (error) {
            swal('Oops', t('common.check-form-fields'), "error");
            return;
        }

        const dictionaryData = {
            code: code,
            reference: reference,
            nameFr: nameFr,
            nameEn: nameEn,
            parent: parent
        };

        if (updateMode) {
            dispatch(updateDictionaryAction(dataUpdateId, dictionaryData)).then(() => {
                loadDictionaries();
                swal(t('common.success'), t('common.dictionary-updated'), "success");
            }).catch(() => {
                swal('Oops', t('common.update-error'), "error");
            });
        } else {
            dispatch(addDictionaryAction(dictionaryData)).then(() => {
                loadDictionaries();
                swal(t('common.success'), t('common.dictionary-created'), "success");
            }).catch(() => {
                swal('Oops', t('common.create-error'), "error");
            });
        }
        
        setAddModal(false);
        resetForm();
    }

    return (
        <>
            {/* Header */}
            <div className="provider-type-header">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                        <h4 className="mb-1 text-white">{t('common.dictionaries')}</h4>
                        <p className="mb-0 small text-white-50">{t('common.manage-dictionaries')}</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <button
                            type="button"
                            className="btn btn-outline-light btn-sm d-flex align-items-center"
                            onClick={handleDownloadTemplate}
                            style={{ 
                                borderColor: 'rgba(255, 255, 255, 0.7)',
                                color: 'white',
                                fontWeight: '500'
                            }}
                        >
                            <i className="fa fa-download me-2"></i> 
                            {t("common.download-template")}
                        </button>
                        
                        {hasMenu('MENPARAM') && canDo('MENPARAM', 'ADDDICTIONARY') && (
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm d-flex align-items-center"
                                onClick={() => {
                                    resetForm();
                                    setAddModal(true);
                                }}
                                style={{ 
                                    backgroundColor: 'white',
                                    color: '#4e73df',
                                    fontWeight: '600',
                                    border: 'none'
                                }}
                            >
                                <i className="fa fa-plus-circle me-2"></i> 
                                {t("common.dictionary")}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal for creating/editing dictionary */}
            <Modal 
                show={addModal} 
                onHide={() => setAddModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5">
                        {updateMode ? t("common.update-dictionary") + " " + nameFr : t("common.new-dictionary")}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <form>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">{t('common.code')} <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                                        placeholder="DIC01"
                                        value={code}
                                        disabled={showLoading || showProvLoading}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                    {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">{t('common.reference')} <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.reference ? 'is-invalid' : ''}`}
                                        placeholder="dictionnaire"
                                        value={reference}
                                        disabled={showLoading || showProvLoading}
                                        onChange={(e) => setReference(e.target.value)}
                                    />
                                    {errors.reference && <div className="invalid-feedback">{errors.reference}</div>}
                                </div>
                            </div>
                        </div>
                        
                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">{t('common.name-fr')} <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.nameFr ? 'is-invalid' : ''}`}
                                        placeholder="Banque A-Care"
                                        value={nameFr}
                                        disabled={showLoading || showProvLoading}
                                        onChange={(e) => setNameFr(e.target.value)}
                                    />
                                    {errors.nameFr && <div className="invalid-feedback">{errors.nameFr}</div>}
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">{t('common.name-en')} <small className="text-muted">({t('common.optional')})</small></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="A-Care Bank"
                                        value={nameEn}
                                        disabled={showLoading || showProvLoading}
                                        onChange={(e) => setNameEn(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">{t('common.parent')} <small className="text-muted">({t('common.optional')})</small></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t('common.enter-parent')}
                                value={parent}
                                disabled={showLoading || showProvLoading}
                                onChange={(e) => setParent(e.target.value)}
                            />
                        </div>
                    </form>
                </Modal.Body>
                
                <Modal.Footer className="border-top">
                    <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => setAddModal(false)}
                        disabled={showLoading || showProvLoading}
                    >
                        {t('common.cancel')}
                    </button>
                    
                    {(showLoading || showProvLoading) ? (
                        <Button variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="ms-2">{t('common.saving')}</span>
                        </Button>
                    ) : (
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={onDictionarySave}
                        >
                            {t('common.save')}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* DataTable */}
            <div className="card border-0 shadow-sm bg-white">
                <div className="card-body p-0">
                    <DataTableBase 
                        data={dictionaries.map(dict => {
                            dict.createdAt = new Date(dict.createdAt ? dict.createdAt : dict.created);
                            return dict;
                        })}
                        emptyMessage={t('common.no-dictionaries')}
                        filters={filters}
                        globalFiltersFields={['nameFr', 'nameEn', 'code', 'reference']}
                        header={renderHeader()}
                        columns={setColumns()}
                        exportColumns={[
                            { title: t('common.code'), dataKey: 'code' },
                            { title: t('common.reference'), dataKey: 'reference' },
                            { title: t('common.name-fr'), dataKey: 'nameFr' },
                            { title: t('common.name-en'), dataKey: 'nameEn' },
                            { title: t('common.parent'), dataKey: 'parent' },
                            { title: t('common.created-on'), dataKey: 'createdAt' },
                        ]}
                        rows={10} 
                        showExport={true}
                        exportFilename="dictionnaires"
                        loading={showLoading}
                        lazy={false}
                    />
                </div>
            </div>
        </>
    );
};

export default DictionaryTab;